<template>
  <div
    class="absolute z-10 min-w-130 bg-white px-4 py-2 shadow-md"
    data-cy="app-token-modal"
    :style="positionStyle"
  >
    <div class="flex flex-col space-y-2">
      <tracking-token-inputs
        v-if="type === DynamicFieldModalType.Resume"
        @value:change="tokenValues = $event"
      />
      <widget-token-inputs v-else @value:change="widgetValues = $event" />
      <utm-parameter-selector @value:change="utmValues = $event" />
      <div class="flex w-full items-center justify-end space-x-3">
        <button
          :class="hasSomeValue ? 'text-primary' : 'text-disabled-medium'"
          data-cy="app-token-save"
          :disabled="!hasSomeValue"
          @click.prevent="saveValues"
        >
          {{ $t("COMMON.SAVE") }}
        </button>
        <button
          class="text-error"
          data-cy="app-token-cancel"
          @click.prevent="emit('value:save', '')"
        >
          {{ $t("COMMON.CANCEL") }}
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";

import { DynamicFieldModalType } from "@/enums/emailCustomization";
import { UTM_DYNAMIC_PARAMETERS_MAP } from "@/helpers/constants/tracking";
import { appTrackingTokenKey } from "@/helpers/textEditor";

import TrackingTokenInputs from "@/components/dynamicFields/TrackingTokenInputs.vue";
import UtmParameterSelector from "@/components/dynamicFields/UtmParameterSelector.vue";
import WidgetTokenInputs from "@/components/dynamicFields/WidgetTokenInputs.vue";

const { position, type } = defineProps<{
  position: {
    bottom: number;
    left: number;
    right: number;
    top: number;
    height: number;
  };
  type: DynamicFieldModalType;
}>();

const emit = defineEmits<{
  "value:save": [value: string];
}>();

const layoutValues = {
  barHeight: 45,
  leftOffset: 20,
  leftOverlapOffset: 150,
  maxAvailableWidth: 900,
} as const;

const tokenValues = ref<{ value: string; id: string }[]>([]);
const utmValues = ref<{ type: string; value: string; id: string }[]>([]);
const widgetValues = ref<{ value: string; id: string }[]>([]);

const hasSomeValue = computed(
  () =>
    !!tokenValues.value.map(({ value }) => !!value).some(Boolean) ||
    utmValues.value.map(({ value }) => !!value).some(Boolean) ||
    widgetValues.value.map(({ value }) => !!value).some(Boolean)
);

const positionStyle = computed(() => {
  const topPosition = position.top + position.height + layoutValues.barHeight;
  const leftPosition =
    position.left > layoutValues.maxAvailableWidth
      ? position.left - layoutValues.leftOverlapOffset
      : position.left - layoutValues.leftOffset;
  return {
    top: `${topPosition}px`,
    left: `${leftPosition}px`,
  };
});

const isResumeField = computed(() => type === DynamicFieldModalType.Resume);

const getValues = (
  values: { value: string; id: string; type?: string }[],
  type: "resume" | "widget" | "utm"
) => {
  return values.reduce((tokens: string[], { id, value, type: tokenType }) => {
    if (!value) {
      return tokens;
    }

    const typeKey = {
      resume: appTrackingTokenKey,
      widget: id,
      utm: `${UTM_DYNAMIC_PARAMETERS_MAP[tokenType as keyof typeof UTM_DYNAMIC_PARAMETERS_MAP]}`,
    };

    tokens.push(`${typeKey[type]}${value}`);
    return tokens;
  }, []);
};

const saveValues = () => {
  const resumeTokensWithValues = getValues(tokenValues.value, "resume");

  const widgetTokensWithValues = getValues(widgetValues.value, "widget");

  const utmTokensWithValues = getValues(utmValues.value, "utm");

  const mainTokensWithValues = isResumeField.value
    ? resumeTokensWithValues
    : widgetTokensWithValues;

  const value = `__${[...mainTokensWithValues, ...utmTokensWithValues].join(",")}`;

  emit("value:save", value);
};
</script>
